import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import Image from 'next/image';
import CtaButton from '../ctaButton/ctaButton';
import Reveal from '../reveal/reveal';
import CardLeftBackgroundSvg from './images/card-left-background.svg';
import CardRightBackgroundSvg from './images/card-right-background.svg';
import MaskGroupSvg from './images/mask-group.svg';
import MaskGroupMobileSvg from './images/mask-group-mobile.svg';
import MobilePhoneSvg from './images/mobile-phone.svg';
import styles from './homepagePromoCard.module.scss';

function HomepagePromoCard({ cards = [], enableAnimations = false }) {
  if (!cards || cards.length === 0) {
    return null;
  }

  if (cards.length === 2) {
    const leftCard = cards[0];
    const rightCard = cards[1];

    return (
      <div className={styles.wrapper}>
        <div className={styles.homepagePromoCard}>
          <Reveal
            variant="left"
            enabled={enableAnimations}
            className={styles.card}
            style={{ background: leftCard.gradient }}
          >
            <Image
              src={CardLeftBackgroundSvg}
              alt=""
              fill
              style={{ objectFit: 'cover' }}
            />
            <div className={styles.cardContent}>
              {leftCard.title && (
                <h2 className={styles.cardTitle}>{parse(leftCard.title)}</h2>
              )}
              {leftCard.description && (
                <p className={styles.cardDescription}>
                  {parse(leftCard.description)}
                </p>
              )}
              {leftCard.cta && (
                <div className={styles.cardCtaWrapper}>
                  <CtaButton
                    label={leftCard.cta.label}
                    href={leftCard.cta.link}
                    variant={leftCard.cta.variant || 'secondary white'}
                    color={leftCard.cta.color}
                  />
                </div>
              )}
            </div>
          </Reveal>
          <Reveal
            variant="right"
            delay={120}
            enabled={enableAnimations}
            className={styles.card}
            style={{ background: rightCard.gradient }}
          >
            <div className={styles.cardRightBackground}>
              <Image
                src={CardRightBackgroundSvg}
                alt=""
                fill
                style={{ objectFit: 'contain', objectPosition: 'bottom right' }}
              />
            </div>
            <div className={styles.cardContent}>
              {rightCard.title && (
                <h2 className={styles.cardTitle}>{parse(rightCard.title)}</h2>
              )}
              {rightCard.description && (
                <p className={styles.cardDescription}>
                  {parse(rightCard.description)}
                </p>
              )}
              {rightCard.cta && (
                <div className={styles.cardCtaWrapper}>
                  <CtaButton
                    label={rightCard.cta.label}
                    href={rightCard.cta.link}
                    variant={rightCard.cta.variant || 'secondary white'}
                    color={rightCard.cta.color}
                  />
                </div>
              )}
            </div>
            <div className={styles.cardMobilePhone}>
              <Image src={MobilePhoneSvg} alt="" width={373} height={437} />
            </div>
          </Reveal>
        </div>
      </div>
    );
  }

  if (cards.length === 1) {
    const card = cards[0];

    return (
      <div className={styles.wrapper}>
        <div className={styles.homepagePromoCard}>
          <Reveal
            variant="scale"
            enabled={enableAnimations}
            className={`${styles.card} ${styles.cardSingle}`}
            style={{ background: card.gradient }}
          >
            <div className={styles.cardMaskBackgroundDesktop}>
              <Image
                src={MaskGroupSvg}
                alt=""
                fill
                style={{ objectFit: 'cover', objectPosition: 'right center' }}
                priority
              />
            </div>
            <div className={styles.cardMaskBackgroundMobile}>
              <Image
                src={MaskGroupMobileSvg}
                alt=""
                fill
                style={{ objectFit: 'cover', objectPosition: 'bottom right' }}
                priority
              />
            </div>
            <div className={styles.cardRightBackgroundSingle}>
              <Image
                src={CardRightBackgroundSvg}
                alt=""
                fill
                style={{ objectFit: 'contain', objectPosition: 'bottom right' }}
              />
            </div>
            <div className={styles.cardContentSingle}>
              {card.title && (
                <h2 className={styles.cardTitle}>{parse(card.title)}</h2>
              )}
              {card.description && (
                <p className={styles.cardDescriptionSingle}>
                  {parse(card.description)}
                </p>
              )}
              {card.cta && (
                <div className={styles.cardCtaWrapper}>
                  <CtaButton
                    label={card.cta.label}
                    href={card.cta.link}
                    variant={card.cta.variant || 'secondary white'}
                    color={card.cta.color}
                  />
                </div>
              )}
            </div>
            <div className={styles.cardMobilePhoneSingle}>
              <Image src={MobilePhoneSvg} alt="" width={373} height={437} />
            </div>
          </Reveal>
        </div>
      </div>
    );
  }

  return null;
}

HomepagePromoCard.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      gradient: PropTypes.string,
      image: PropTypes.shape({
        url: PropTypes.string,
        alt: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
      }),
      cta: PropTypes.shape({
        label: PropTypes.string,
        link: PropTypes.string,
        variant: PropTypes.string,
        color: PropTypes.string,
      }),
    })
  ),
  enableAnimations: PropTypes.bool,
};

export default HomepagePromoCard;
